(function () {
  if (window.iGardenDiscounts) return;

  let appliedDiscounts = [];
  let pendingDiscountRemovals = [];
  const persistentDiscountCodes = new Set();
  const productBoundDiscountCodes = new Set();
  const maxDiscountCodes = 5;
  let cartUpdateUnsubscriber;
  let initializationPromise;

  function mergeDiscountLists(...lists) {
    const seen = new Set();
    const merged = [];

    lists.forEach((list) => {
      (list || []).forEach((code) => {
        const normalized = String(code || '').trim();
        if (normalized && !seen.has(normalized)) {
          seen.add(normalized);
          merged.push(normalized);
        }
      });
    });

    return merged.slice(0, 100);
  }

  function normalizeDiscountCodes(discountCodes) {
    const values = Array.isArray(discountCodes) ? discountCodes : [discountCodes];
    return mergeDiscountLists(
      values.flatMap((code) => String(code || '').split(','))
    );
  }

  function codesFromCart(cart) {
    return (cart.discount_codes || [])
      .filter((entry) => typeof entry === 'string' || entry.applicable !== false)
      .map((entry) => (typeof entry === 'string' ? entry : entry.code))
      .filter(Boolean);
  }

  function normalizeDiscountLabel(text) {
    return String(text || '').trim().split(/\s+/)[0];
  }

  function registerPersistentCode(code) {
    const normalized = String(code || '').trim();
    if (normalized) persistentDiscountCodes.add(normalized);
  }

  function registerProductBoundCode(code) {
    const normalized = String(code || '').trim();
    if (normalized) productBoundDiscountCodes.add(normalized);
  }

  function getLineDiscountCodes(cartItem) {
    if (!cartItem) return [];

    const codes = [];
    const productCode = (cartItem.dataset.productDiscount || '').trim();
    if (productCode) codes.push(productCode);

    [...cartItem.querySelectorAll('.discounts__discount')].forEach((element) => {
      const label = normalizeDiscountLabel(element.textContent);
      if (label && productBoundDiscountCodes.has(label)) {
        codes.push(label);
      }
    });

    return mergeDiscountLists(codes);
  }

  function discountFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('discount') || params.get('discount_code') || '';
  }

  function buildEffectiveDiscountList(cart) {
    return mergeDiscountLists(
      [...persistentDiscountCodes],
      discountFromUrl() ? [discountFromUrl()] : [],
      [...productBoundDiscountCodes],
      appliedDiscounts,
      cart ? codesFromCart(cart) : []
    );
  }

  function formatMoney(cents, currency) {
    const currencyCode = String(currency || 'USD').toUpperCase();
    const amount = cents / 100;

    if (currencyCode === 'AUD') {
      return `AUD $${amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }

    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode
    });
  }

  function updatePricesFromCart(cart) {
    if (!cart) return;

    const truePriceElement = document.querySelector('.totals__total-value-true');
    if (truePriceElement) {
      truePriceElement.textContent = formatMoney(cart.total_price, cart.currency);
    }

    const originalPriceElement = document.querySelector('.totals__total-value-original');
    if (originalPriceElement) {
      originalPriceElement.textContent = formatMoney(cart.original_total_price, cart.currency);
      originalPriceElement.style.display = cart.total_discount === 0 ? 'none' : 'inline';
    }
  }

  function saveAndUpdate(discountArray, requestOptions = {}) {
    appliedDiscounts = mergeDiscountLists(discountArray);
    const effectiveList = buildEffectiveDiscountList();
    appliedDiscounts = effectiveList;
    return updateCartDiscounts(effectiveList, false, requestOptions);
  }

  async function updateCartDiscounts(discountArray, isCleanup = false, requestOptions = {}) {
    try {
      const requestedCodes = mergeDiscountLists(discountArray).slice(0, maxDiscountCodes);
      const payload = { discount: requestedCodes.join(',') };
      const requestedSections = Array.isArray(requestOptions.sections)
        ? requestOptions.sections.filter(Boolean).slice(0, 5)
        : [];

      if (requestedSections.length > 0) {
        payload.sections = requestedSections;
        payload.sections_url = requestOptions.sectionsUrl || window.location.pathname;
      }

      const cartUpdateRoute = window.routes?.cart_update_url || '/cart/update';
      const cartUpdateAjaxUrl = cartUpdateRoute.endsWith('.js')
        ? cartUpdateRoute
        : `${cartUpdateRoute}.js`;
      const response = await fetch(cartUpdateAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Update cart discounts failed');

      const cart = await response.json();
      const discountEntries = cart.discount_codes || [];
      const invalidCodes = discountEntries
        .filter((entry) => typeof entry !== 'string' && entry.applicable === false)
        .map((entry) => entry.code)
        .filter(Boolean);
      const validCodes = discountEntries
        .filter((entry) => typeof entry === 'string' || entry.applicable !== false)
        .map((entry) => (typeof entry === 'string' ? entry : entry.code))
        .filter(Boolean)
        .slice(0, maxDiscountCodes);

      invalidCodes.forEach((code) => {
        if (!persistentDiscountCodes.has(code)) {
          productBoundDiscountCodes.delete(code);
        }
      });

      appliedDiscounts = validCodes;
      localStorage.setItem('cart_discounts', JSON.stringify(validCodes));

      if (!isCleanup && invalidCodes.length > 0) {
        return updateCartDiscounts(validCodes, true, requestOptions);
      }

      updatePricesFromCart(cart);
      return cart;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function syncAppliedDiscountsFromCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Unable to load cart discounts');

    const cart = await response.json();
    const fromUrl = discountFromUrl();
    if (fromUrl) registerPersistentCode(fromUrl);

    appliedDiscounts = buildEffectiveDiscountList(cart);
    JSON.parse(localStorage.getItem('cart_discounts') || '[]').forEach((code) => {
      registerProductBoundCode(code);
    });
    appliedDiscounts = buildEffectiveDiscountList(cart);
    return cart;
  }

  async function initAppliedDiscounts() {
    const fromUrl = discountFromUrl();
    if (fromUrl) registerPersistentCode(fromUrl);

    const fromStorage = JSON.parse(localStorage.getItem('cart_discounts') || '[]');
    fromStorage.forEach((code) => {
      if (!fromUrl || code !== fromUrl) {
        registerProductBoundCode(code);
      }
    });

    try {
      const cart = await syncAppliedDiscountsFromCart();
      appliedDiscounts = buildEffectiveDiscountList(cart);
      localStorage.setItem('cart_discounts', JSON.stringify(appliedDiscounts));
      updatePricesFromCart(cart);
    } catch (error) {
      console.error(error);
    }
  }

  async function applyCartDiscountCodes(discountCodes, requestOptions = {}) {
    const normalizedCodes = normalizeDiscountCodes(discountCodes);
    if (!normalizedCodes.length) return null;

    await syncAppliedDiscountsFromCart();
    normalizedCodes.forEach(registerProductBoundCode);
    appliedDiscounts = mergeDiscountLists(normalizedCodes, appliedDiscounts);
    return saveAndUpdate(appliedDiscounts, requestOptions);
  }

  function applyCartDiscountCode(discountCode, requestOptions = {}) {
    return applyCartDiscountCodes([discountCode], requestOptions);
  }

  async function resetCartDiscounts(cart, requestOptions = {}) {
    pendingDiscountRemovals = [];
    productBoundDiscountCodes.clear();

    const fromUrl = discountFromUrl();
    if (fromUrl) registerPersistentCode(fromUrl);

    appliedDiscounts = mergeDiscountLists(
      [...persistentDiscountCodes],
      fromUrl ? [fromUrl] : []
    );
    localStorage.setItem('cart_discounts', JSON.stringify(appliedDiscounts));

    if (requestOptions.updateCart === false) {
      updatePricesFromCart(cart);
      return cart;
    }

    return (await updateCartDiscounts(appliedDiscounts, false, requestOptions)) || cart;
  }

  function bindLegacyAddToCart() {
    document.querySelectorAll('.buy_add_to_cart').forEach((button) => {
      if (button.dataset.discountBound) return;
      button.dataset.discountBound = '1';

      button.addEventListener('click', async function (event) {
        if (this.dataset.discountAfterAdd === 'true') return;
        const productFormSelector = 'product-form, product-forms, form[data-type="add-to-cart-form"]';
        if (event.target?.closest?.(productFormSelector)) return;
        if (this.closest(productFormSelector) || this.querySelector(productFormSelector)) return;
        if (this.matches('[data-selection-add], [data-card-add-to-cart], [data-card-buy-with-accessories], [data-igarden-cart-managed]')) return;

        const discountCode = (this.dataset.discount || '').trim();
        if (!discountCode) return;

        try {
          await applyCartDiscountCode(discountCode);
        } catch (error) {
          console.error(error);
        }
      });
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      const button = event.target.closest('cart-remove-button');
      if (!button) return;
      pendingDiscountRemovals = getLineDiscountCodes(button.closest('.cart-item'));
    },
    true
  );

  function bindCartUpdateSync() {
    if (typeof subscribe !== 'function') return;
    cartUpdateUnsubscriber?.();

    cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event.cartData) return;

      const cart = event.finalCart || event.cartData;

      if (event.source === 'cart-items' && event.cartAction === 'change') {
        pendingDiscountRemovals = [];
        updatePricesFromCart(cart);
        return;
      }

      if (event.source === 'cart-items' && event.cartAction === 'clear') {
        if (pendingDiscountRemovals.length > 0) {
          const toRemove = pendingDiscountRemovals.filter(
            (code) => productBoundDiscountCodes.has(code) && !persistentDiscountCodes.has(code)
          );
          pendingDiscountRemovals = [];

          toRemove.forEach((code) => productBoundDiscountCodes.delete(code));
          appliedDiscounts = appliedDiscounts.filter(
            (code) => !toRemove.includes(code) || persistentDiscountCodes.has(code)
          );

          saveAndUpdate(buildEffectiveDiscountList(cart));
          return;
        }

        appliedDiscounts = mergeDiscountLists(
          [...persistentDiscountCodes],
          discountFromUrl() ? [discountFromUrl()] : [],
          codesFromCart(cart)
        );
        saveAndUpdate(appliedDiscounts);
        return;
      }

      if (event.source === 'product-form') {
        if (!event.finalCart) return;
        appliedDiscounts = buildEffectiveDiscountList(cart);
        localStorage.setItem('cart_discounts', JSON.stringify(appliedDiscounts));
        updatePricesFromCart(cart);
      }
    });
  }

  function init() {
    if (!initializationPromise) initializationPromise = initAppliedDiscounts();
    bindLegacyAddToCart();
    bindCartUpdateSync();
  }

  const api = Object.freeze({
    apply: applyCartDiscountCode,
    applyMany: applyCartDiscountCodes,
    resetCart: resetCartDiscounts,
    sync: syncAppliedDiscountsFromCart,
    refreshTotals: updatePricesFromCart,
    init
  });

  window.iGardenDiscounts = api;
  window.iGardenApplyCartDiscount = applyCartDiscountCode;

  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
